import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { INDUSTRY_SECTION_KEYS, findSection } from "../services/contentApi";

const IndustryPageContext = createContext(null);

export function IndustryPageProvider({ fetchPage, children }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setPage(null);
    setError(null);

    fetchPage()
      .then((data) => {
        if (!cancelled) {
          setPage(data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setPage(null);
        }
        console.error("Error fetching industry page:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fetchPage]);

  const sections = useMemo(() => {
    const result = {};
    INDUSTRY_SECTION_KEYS.forEach((key) => {
      result[key] = findSection(page, key);
    });
    return result;
  }, [page]);

  const value = useMemo(
    () => ({ page, sections, loading, error }),
    [page, sections, loading, error]
  );

  return (
    <IndustryPageContext.Provider value={value}>
      {children}
    </IndustryPageContext.Provider>
  );
}

export function useIndustryPage() {
  const ctx = useContext(IndustryPageContext);
  if (!ctx) {
    throw new Error("useIndustryPage must be used within an industry page provider");
  }
  return ctx;
}

export function useIndustrySection(key) {
  const ctx = useContext(IndustryPageContext);
  if (!ctx) {
    return { section: null, loading: false, fromIndustryPage: false };
  }
  return {
    section: ctx.sections[key] ?? null,
    loading: ctx.loading,
    fromIndustryPage: true,
  };
}

export const useIndustriesSection = useIndustrySection;
